/**
 * Data layer for `self-serve-credit-card-subscribe`. Thin typed fetch wrappers over /api/v1/self-serve-credit-card-subscribe.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface SelfServeCreditCardSubscribeRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSelfServeCreditCardSubscribeResponse {
	count: number;
	items: SelfServeCreditCardSubscribeRow[];
}

export interface CreateSelfServeCreditCardSubscribeBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/self-serve-credit-card-subscribe";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSelfServeCreditCardSubscribeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSelfServeCreditCardSubscribeResponse>(res);
}

export async function create(body: CreateSelfServeCreditCardSubscribeBody): Promise<SelfServeCreditCardSubscribeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<SelfServeCreditCardSubscribeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const selfServeCreditCardSubscribeQueryKeys = {
	all: ["self-serve-credit-card-subscribe"] as const,
	list: () => [...selfServeCreditCardSubscribeQueryKeys.all] as const,
	detail: (id: string) => [...selfServeCreditCardSubscribeQueryKeys.all, id] as const,
};
